import {useQuery} from "@tanstack/react-query";
import React, {useEffect, useState} from "react";
import LoadingElement from "@/components/Loading/LoadingElement";
import {toast} from "@/components/ui/use-toast";
import Navbar from "@/components/Navbar/Navbar";
import SideMenu from "@/components/MyAccount/SideMenu";
import ApiHttpClient from "@/HttpClient";
import {fetchShippingCsvs, ShippingCSVListResponseItem} from "@/api/ShippingsAPI";
import {read, write, WorkSheet, utils} from 'xlsx';
import {Modal} from "@/components/ui/modal";
import DataGrid, {Column, SortColumn} from "react-data-grid";
import 'react-data-grid/lib/styles.css';
import "./ShippingDataGridModal.css";
import { getDecodedTokenDetails } from "@/components/Token/TokenService";

const handleDownloadShippingCSV = async (shippings: number[], date: string, workingPointId: string, excelInsteadOfCsv = false) => {
    try {
        const response = await ApiHttpClient().post(`/shipping/csv/download`, shippings);

        if (response.status === 200) {
            const csv = response.data as string;

            // Create a Blob
            const blob = !excelInsteadOfCsv ? new Blob([csv], {
                type: 'text/csv',
                endings: 'native'
            }) : new Blob([write(read(csv, {type: "string"}), {
                bookType: "xlsx",
                type: "array"
            })], {type: 'application/octet-stream'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const filename = "shipping_csv_" + workingPointId + "_" + date + (!excelInsteadOfCsv ? '.csv' : '.xlsx');
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } else {
            console.error('A aparut o eroare la descarcarea csv-ului, mesajul:', response.status);
        }
    } catch (error) {
        console.error('A aparut o eroare la descarcarea csv-ului', error);
    }
}

const handleDownloadShippingPDF = async (shippings: number[]) => {
    try {
        const response = await ApiHttpClient().post(`shipping/pdf/download`, shippings);

        if (response.status === 200) {
            const {pdfBytes, fileName} = response.data;
            // Decode the base64 string
            const byteCharacters = atob(pdfBytes);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            // Create a Blob from the byteArray
            const blob = new Blob([byteArray], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', fileName || 'Shipping.pdf');
            document.body.appendChild(link);
            link.click();

            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } else {
            console.error('A aparut o eroare la descarcarea pdf-ului, mesajul:', response.status);
        }
    } catch (error) {
        console.error('A aparut o eroare la descarcarea pdf-ului', error);
    }
}

type Row = any[];
type AOAColumn = Column<Row>;
type RowCol = { rows: Row[]; columns: AOAColumn[]; };

function ws_to_rdg(ws: WorkSheet): RowCol {
    /* create an array of arrays */
    const rows = utils.sheet_to_json(ws, {header: 1}) as Row[];

    /* create column array */
    const range = utils.decode_range(ws["!ref"] || "A1");
    const columns = Array.from({length: range.e.c + 1}, (_, i) => ({
        key: String(i), // RDG will access row["0"], row["1"], etc
        name: utils.encode_col(i), // the column labels will be A, B, etc
    }));

    return {rows, columns}; // these can be fed to setRows / setColumns
}

export default function ShippingCsvListPage() {
    const [currentPage, setCurrentPage] = useState(0);
    const [userRole, setUserRole] = useState("BASIC");
    const [userEmail, setUserEmail] = useState("");
    const [toggleDetails, setToggleDetails] = useState(false);
    const [shippingsData, setShippingsData] = useState<ShippingCSVListResponseItem | null>(null);
    const [detailsData, setDetailsData] = useState<RowCol | null>(null);
    const [inputValue, setInputValue] = useState(currentPage + 1);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const pageSize = 100;

    const [rows, setRows] = useState(detailsData?.rows || []);
    const [sortColumns, setSortColumns] = useState<SortColumn[]>([]);

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        if (data?.totalPages && value > data.totalPages) {
            value = data.totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValue(value);

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {
            if (data?.totalPages && value >= 1 && value <= data?.totalPages) {
                handlePageChange(value - 1);
            }
        }, 500);

        setDebounceTimer(timer);
    };
    
    useEffect(() => {
                (async () => {
                    const tokenDetails = await getDecodedTokenDetails();
                    
                    if (tokenDetails?.token) {
                        try {
                            const userRole = tokenDetails.userRole;
                            const userEmail = tokenDetails.userEmail;
            
                            setUserRole(userRole);
                            setUserEmail(userEmail);
                        } catch (error) {
                            console.error('Failed to decode token', error);
                        }
                    } else {
                        console.error('No token found');
                    }
                })();
            }, []);
   

    const handlePageChange = (selectedPage: any) => {
        setCurrentPage(selectedPage);
    };

    const {data, isLoading, error} = useQuery({
        queryKey: ["products", currentPage + 1, pageSize],
        queryFn: fetchShippingCsvs,
    });

    if (isLoading) return <p className="h-screen w-screen flex items-center justify-center"><LoadingElement/></p>;
    if (error || !data) {
        toast({description: "Exista o eroare la preluarea csv-urilor"});
        return <></>;
    }
    
    const handleSort = (newSortColumns: SortColumn[]) => {
        setSortColumns(newSortColumns);

        if (newSortColumns.length === 0) {
            setRows(detailsData!.rows);
            return;
        }

        const sortedRows = [...rows].sort((a: any, b: any) => {
            for (const { columnKey, direction } of newSortColumns) {
                
                const valueA = a[columnKey] ?? '';
                const valueB = b[columnKey] ?? '';

                let compareResult = 0;
                if (typeof valueA === 'number' && typeof valueB === 'number') {
                    compareResult = valueA - valueB;
                } else {
                    compareResult = String(valueA).localeCompare(String(valueB));
                }

                if (compareResult !== 0) {
                    return direction === 'ASC' ? compareResult : -compareResult;
                }
            }
            return 0;
        });
        
        setRows(sortedRows);
    };

    
    return (
        <>
            {toggleDetails &&
                <Modal show={toggleDetails} onClose={() => setToggleDetails(false)}
                       popup id="details-data-grid-modal">
                    <Modal.Header>
                        {"shipping_csv_" + shippingsData!.workingPointId + "_" + shippingsData!.date?.split("T")[0]}
                    </Modal.Header>
                    <Modal.Body>
                        {
                            !detailsData && (
                                <div className="text-center mt-4">
                                    <LoadingElement/>
                                </div>
                            )
                        }
                        {
                            detailsData && (
                                // <DataGrid columns={detailsData!.columns}
                                //           rows={detailsData!.rows}
                                //           className="details-data-grid"
                                //           // sortColumns={sortColumns}
                                //           // onSortColumnsChange={onSortColumnsChange}
                                //           // direction={direction}
                                //           // defaultColumnOptions={{ width: '1fr' }}
                                //           // onColumnsReorder={onColumnsReorder}
                                // />
                                <DataGrid
                                    columns={detailsData!.columns.map(col => ({ ...col, sortable: true }))}
                                    rows={detailsData!.rows}
                                    sortColumns={sortColumns}
                                    onSortColumnsChange={handleSort}
                                />
                            )
                        }
                    </Modal.Body>
                </Modal>}
            <Navbar/>
            <div className="flex flex-row items-start">
                <SideMenu/>
                {/* Desktop Version */}
                <div className="hidden w-full md:block z-auto">
                    <div className="relative overflow-x-auto mx-8 mt-8 shadow-md sm:rounded-lg">
                        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead
                                className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                            <tr>
                                <th scope="col" className="px-6 py-3">
                                    Data
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Ultima actualizare
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Punct de lucru
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl">
                                    Livrari
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl">
                                    Precomenzi
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Descarca
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {data.items.map((shippingCSV: ShippingCSVListResponseItem) => {
                                return (
                                    <tr className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700">
                                        <td className="px-6 py-4">
                                            {shippingCSV.date?.split("T")[0]}
                                        </td>
                                        <td className="px-6 py-4">
                                            {new Date(shippingCSV.lastUpdated).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            {shippingCSV.workingPointName}
                                        </td>
                                        <th scope="row"
                                            className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap  dark:text-white max-w-xl align-top">
                                            <div className="flex flex-wrap overflow-hidden justify-start">
                                                {shippingCSV.shippings.map((shipping: number) =>
                                                    <a href={"/my-account/shipping/" + shipping}
                                                       className="text-blue-500 underline z-auto sm:w-1/2 md:w-1/3 lg:w-1/4 mx-2">{shipping}</a>
                                                )}
                                            </div>
                                        </th>
                                        <th scope="row"
                                            className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white max-w-xl align-top">
                                            <div className="flex flex-wrap overflow-hidden justify-start">
                                                {shippingCSV.preOrders.map((preOrder: number) =>
                                                    <a href={"/my-account/preorders/pre-order-detail/" + preOrder}
                                                       className="text-blue-500 underline z-auto m-1 w-1/4 sm:w-1/2 md:w-1/3 lg:w-1/4">{preOrder}</a>
                                                )}
                                            </div>
                                        </th>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col text-center">
                                                <button
                                                    onClick={() => handleDownloadShippingCSV(shippingCSV.shippings, shippingCSV.date?.split("T")[0], shippingCSV.workingPointId)}
                                                    className="mb-1 font-medium text-red-600 hover:text-red-500">
                                                    Download CSV
                                                </button>
                                                <button
                                                    onClick={() => handleDownloadShippingPDF(shippingCSV.shippings)}
                                                    className="mb-1 font-medium text-red-600 hover:text-red-500">
                                                    Download PDF
                                                </button>
                                                {
                                                    userRole === "ADMIN" &&  (
                                                        <button
                                                            onClick={() => handleDownloadShippingCSV(shippingCSV.shippings, shippingCSV.date?.split("T")[0], shippingCSV.workingPointId, true)}
                                                            className="mb-1 font-medium text-red-600 hover:text-red-500">
                                                            Download Excel
                                                        </button>
                                                    )
                                                }
                                                {
                                                    userRole === "ADMIN" && (
                                                        <button
                                                            onClick={async () => {
                                                                setShippingsData(shippingCSV);
                                                                setToggleDetails(true);
                                                                setDetailsData(null);
                                                                try {
                                                                    const response = await ApiHttpClient().post(`/shipping/csv/download`, shippingCSV.shippings);

                                                                    if (response.status === 200) {
                                                                        const csv = response.data as string;
                                                                        const workbook = read(csv, {type: "string"});
                                                                        const data = ws_to_rdg(workbook.Sheets[workbook.SheetNames[0]]);
                                                                        setDetailsData(data);
                                                                    } else {
                                                                        setToggleDetails(false);
                                                                        console.error('A aparut o eroare la descarcarea csv-ului, mesajul:', response.status);
                                                                        toast({description: "A aparut o eroare la descarcarea csv-ului"});
                                                                    }
                                                                } catch (error) {
                                                                    setToggleDetails(false);
                                                                    console.error('A aparut o eroare la descarcarea csv-ului', error);
                                                                    toast({description: "A aparut o eroare la descarcarea csv-ului"});
                                                                }
                                                            }}
                                                            className="mb-1 font-medium text-red-600 hover:text-red-500">
                                                            Vezi Excel
                                                        </button>
                                                    )
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                            </tbody>
                        </table>
                    </div>

                    <div className="text-center mt-8 mb-2">
                        <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage > 0) handlePageChange(currentPage - 1);
                                    }}
                                    className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Previous page"
                                    disabled={currentPage === 0}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>

                            <li>
                                <label htmlFor="Page" className="flex items-center gap-1">
                                    <span className="sr-only">Pagina</span>
                                    <input
                                        type="number"
                                        id="Page"
                                        value={inputValue}
                                        min={1}
                                        max={data.totalPages}
                                        onChange={handleInputChange}
                                        className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                    />
                                    <span className="text-sm">/ {data.totalPages}</span>
                                </label>
                            </li>

                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    }}
                                    className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Next page"
                                    disabled={currentPage === data.totalPages - 1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>
                        </ul>

                    </div>
                </div>

                {/* Mobile Version */}
                <div className="mx-auto max-w-screen-xl md:hidden z-0">
                    {data.items.map((shippingCSV: ShippingCSVListResponseItem) => {
                        return (
                            <div
                                className="flow-root rounded-lg border border-gray-100 py-3 ml-12 mt-8 mb-8 mr-12 shadow-sm">
                                <dl className="-my-3 divide-y divide-gray-100 text-sm">
                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Data</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{shippingCSV.date?.split("T")[0]}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Ultima actualizare</dt>
                                        <dd className="text-gray-700 sm:col-span-2">
                                            {new Date(shippingCSV.lastUpdated).toLocaleString()}
                                        </dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Punct de lucru</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{shippingCSV.workingPointName}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Livrari</dt>
                                        <dd className="text-gray-700 sm:col-span-2">
                                            <div className="flex flex-wrap">
                                                {shippingCSV.shippings.map((shipping: number) =>
                                                    <a href={"/my-account/shipping/" + shipping}
                                                       className="text-blue-500 m-1 underline z-auto w-1/4">{shipping}</a>
                                                )}
                                            </div>
                                        </dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Precomenzi</dt>
                                        <dd className="text-gray-700 sm:col-span-2">
                                            {shippingCSV.preOrders.map((preOrder: number) =>
                                                <a href={"/my-account/preorders/pre-order-detail/" + preOrder}
                                                   className="text-blue-500 m-1 underline z-auto">{preOrder}</a>
                                            )}
                                        </dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Descarca</dt>
                                        <dd className="text-gray-700 sm:col-span-2 flex flex-col">
                                            <button
                                                onClick={() => handleDownloadShippingCSV(shippingCSV.shippings, shippingCSV.date?.split("T")[0], shippingCSV.workingPointId)}
                                                className="mb-1 text-left font-medium text-red-600 hover:text-red-500">
                                                Download CSV
                                            </button>
                                            <button
                                                onClick={() => handleDownloadShippingPDF(shippingCSV.shippings)}
                                                className="mb-1 font-medium text-red-600 hover:text-red-500">
                                                Download PDF
                                            </button>
                                            {
                                                userRole === "ADMIN" && (
                                                    <button
                                                        onClick={() => handleDownloadShippingCSV(shippingCSV.shippings, shippingCSV.date?.split("T")[0], shippingCSV.workingPointId, true)}
                                                        className="mb-1 text-left font-medium text-red-600 hover:text-red-500">
                                                        Download Excel
                                                    </button>
                                                )
                                            }
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        );
                    })}

                    <div className="text-center mb-4">
                        <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage > 0) handlePageChange(currentPage - 1);
                                    }}
                                    className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Previous page"
                                    disabled={currentPage === 0}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>

                            <li>
                                <label htmlFor="Page" className="flex items-center gap-1">
                                    <span className="sr-only">Pagina</span>
                                    <input
                                        type="number"
                                        id="Page"
                                        value={inputValue}
                                        min={1}
                                        max={data.totalPages}
                                        onChange={handleInputChange}
                                        className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                    />
                                    <span className="text-sm">/ {data.totalPages}</span>
                                </label>
                            </li>

                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    }}
                                    className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Next page"
                                    disabled={currentPage === data.totalPages - 1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
}